import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'
import type { DisciplineInfo, DisciplineInKid, DisciplineSchedule } from '@brighterly/lib-core-types/discipline/interfaces'
import type { TutorContact, TutorContactRequest } from '@brighterly/lib-core-types/tutor-contact/interfaces'
import type { DisciplineStore } from '@stores/discipline/interfaces'
import type { ScopeStatus } from '@utils/stores/scoped-entry'

import { Discipline, Stage } from '@brighterly/lib-core-types/discipline/enums'

import { useListsStore } from '@stores/lists'
import { useCustomerStore } from '@stores/me/customer'
import { useKidStore } from '@stores/kid'

import { entryData, entryStatus } from '@utils/stores/scoped-entry'
import { tutor } from '@utils/stores/discipline/getters/tutor'

export const getActiveDiscipline = (state: DisciplineStore): DisciplineInKid | null => {
  const kidId = useKidStore().activeKidId
  const kid = useCustomerStore().customer?.kids?.find(kidInCustomer => kidInCustomer.id === kidId)

  return kid?.disciplines?.find(discipline => discipline.code === state.activeCode) || null
}

export const hasActiveDiscipline = (state: DisciplineStore): boolean => !!getActiveDiscipline(state)?.code

const getActiveId = (state: DisciplineStore): number | null => getActiveDiscipline(state)?.id ?? null

export const hasDisciplineInfo = (state: DisciplineStore): boolean => entryStatus(state.infoById, getActiveId(state)) === 'ready'

export const getDisciplineInfo = (state: DisciplineStore): DisciplineInfo | null => entryData(state.infoById, getActiveId(state))

const getActivationData = (state: DisciplineStore): DisciplineInfo['activation_data'] | null => {
  return getDisciplineInfo(state)?.activation_data || null
}

export const getDisciplineDemo = (state: DisciplineStore): Booking | null => {
  return getDisciplineInfo(state)?.demo || null
}

const getBalance = (state: DisciplineStore): DisciplineInfo['balance'] => getDisciplineInfo(state)?.balance || 0
const getDisciplineCode = (state: DisciplineStore): Discipline | null => getActiveDiscipline(state)?.code || null
const getReversedDisciplineCode = (state: DisciplineStore): Discipline | null => {
  const code = getDisciplineCode(state)
  if (code === Discipline.ELA) {
    return Discipline.Math
  } else if (code === Discipline.Math) {
    return Discipline.ELA
  } else {
    return null
  }
}

const isStageActivation = (state: DisciplineStore): boolean => getDisciplineInfo(state)?.is_on_activation || false

const isStageManualActivation = (state: DisciplineStore): boolean => getDisciplineInfo(state)?.stage === Stage.ManualActivation

const getSettings = (state: DisciplineStore): DisciplineInfo['settings'] | null => getDisciplineInfo(state)?.settings || null

const getUnscheduledBalance = (state: DisciplineStore): DisciplineInfo['unscheduled_balance'] => {
  const balance = getDisciplineInfo(state)?.unscheduled_balance || 0
  return (balance < 0) ? 0 : balance
}

const getShareLessonLink = (state: DisciplineStore): string | null => {
  return getDisciplineInfo(state)?.links?.share_link || null
}

const getLearningPlatformUrl = (state: DisciplineStore): DisciplineInfo['learning_platform_url'] => {
  return getDisciplineInfo(state)?.learning_platform_url || null
}

const getTutorContact = (state: DisciplineStore): TutorContact | null => getDisciplineInfo(state)?.tutor_contact || null

const getTutorContactRequest = (state: DisciplineStore): TutorContactRequest | null => getTutorContact(state)?.request || null

export const getters = {
  activeDiscipline: (state: DisciplineStore): DisciplineInKid | null => getActiveDiscipline(state),
  getTutorContact: (state: DisciplineStore): TutorContact | null => getTutorContact(state),
  getTutorContactRequest: (state: DisciplineStore): TutorContactRequest | null => getTutorContactRequest(state),
  getTutorContactStatus: (state: DisciplineStore): TutorContactRequest['status'] | null => getTutorContactRequest(state)?.status || null,
  getTutorContactNextLessonStart: (state: DisciplineStore): TutorContactRequest['next_lesson_start'] => getTutorContactRequest(state)?.next_lesson_start || null,
  canRequestTutorContact: (state: DisciplineStore): boolean => getTutorContact(state)?.can_request || false,
  hasActiveDiscipline: (state: DisciplineStore): boolean => hasActiveDiscipline(state),
  hasDisciplineInfo: (state: DisciplineStore): boolean => hasDisciplineInfo(state),
  getDisciplineInfo: (state: DisciplineStore): DisciplineInfo | null => getDisciplineInfo(state),
  getInfoStatus: (state: DisciplineStore) => (id: number | null): ScopeStatus => entryStatus(state.infoById, id),
  isInfoLoading: (state: DisciplineStore): boolean => entryStatus(state.infoById, getActiveId(state)) === 'loading',
  getDisciplineDemo: (state: DisciplineStore): Booking | null => getDisciplineDemo(state),
  getUnscheduledBalance: (state: DisciplineStore): DisciplineInfo['unscheduled_balance'] => getUnscheduledBalance(state),
  isUnscheduledBalance: (state: DisciplineStore): boolean => getUnscheduledBalance(state) > 0,
  getBalance: (state: DisciplineStore): DisciplineInfo['balance'] => getBalance(state),
  isZeroBalance: (state: DisciplineStore): boolean => getBalance(state) <= 0,
  isStageActivation: (state: DisciplineStore): boolean => isStageActivation(state),
  isStageManualActivation: (state: DisciplineStore): boolean => isStageManualActivation(state),
  isActivation: (state: DisciplineStore): boolean => isStageActivation(state) || isStageManualActivation(state),
  getDisciplineCode: (state: DisciplineStore): Discipline | null => getDisciplineCode(state),
  getKidName: (state: DisciplineStore): DisciplineInfo['kid']['name'] => getDisciplineInfo(state)?.kid?.name || 'child',
  getKidGrade: (state: DisciplineStore): DisciplineInfo['kid']['grade'] | null => getDisciplineInfo(state)?.kid?.grade || null,
  getReversedDisciplineCode: (state: DisciplineStore): Discipline | null => getReversedDisciplineCode(state),
  getDisciplineCodeLabel: (state: DisciplineStore): string => {
    const listsStore = useListsStore()
    const code = getDisciplineCode(state)
    return code
      ? (listsStore.getDiscipline?.code[code] || code)
      : ''
  },
  getDisciplineCodeForCustomer: (state: DisciplineStore): string => {
    const listsStore = useListsStore()
    const code = getDisciplineCode(state)
    return code
      ? (listsStore.getDiscipline?.code_for_customer[code] || code)
      : ''
  },
  getReversedDisciplineCodeLabel: (state: DisciplineStore): string => {
    const listsStore = useListsStore()
    const code = getReversedDisciplineCode(state)
    return code
      ? (listsStore.getDiscipline?.code[code] || code)
      : ''
  },
  isMath: (state: DisciplineStore): boolean => getActiveDiscipline(state)?.code === Discipline.Math,
  isEla: (state: DisciplineStore): boolean => getActiveDiscipline(state)?.code === Discipline.ELA,
  isReversedMath: (state: DisciplineStore): boolean => {
    const code = getReversedDisciplineCode(state)
    return code === Discipline.Math
  },
  isReversedEla: (state: DisciplineStore): boolean => {
    const code = getReversedDisciplineCode(state)
    return code === Discipline.ELA
  },
  isMathLanding: (state: DisciplineStore): boolean => state.mathLanding,
  isElaLanding: (state: DisciplineStore): boolean => state.elaLanding,
  isLanding: (state: DisciplineStore): boolean => state.elaLanding || state.mathLanding,
  getAvailablePace: (state: DisciplineStore): DisciplineInfo['available_pace'] => getDisciplineInfo(state)?.available_pace || 0,
  getActivationData: (state: DisciplineStore): DisciplineInfo['activation_data'] | null => getActivationData(state),
  getSettings: (state: DisciplineStore): DisciplineInfo['settings'] | null => getSettings(state),
  isSkillReportCreatedAt: (state: DisciplineStore): boolean => !!getSettings(state)?.skill_report_created_at,
  isSkillReportViedAt: (state: DisciplineStore): boolean => !!getSettings(state)?.skill_report_viewed_at,
  isTutorChangeRequest: (state: DisciplineStore): boolean => !!getSettings(state)?.tutor_change_request,
  isFilesCounterVisible: (state: DisciplineStore): boolean => !!getDisciplineInfo(state)?.files_new,
  isShowFirstUpsaleModal: (state: DisciplineStore): boolean => getDisciplineInfo(state)?.settings?.show_upsale_modal || false,
  isShowSecondUpsaleModal: (state: DisciplineStore): boolean => getDisciplineInfo(state)?.settings?.show_second_upsale_modal || false,
  getShareLessonLink: (state: DisciplineStore): string | null => getShareLessonLink(state),
  isShareLessonLink: (state: DisciplineStore): boolean => !!getShareLessonLink(state),
  getLearningPlatformUrl: (state: DisciplineStore): DisciplineInfo['learning_platform_url'] => getLearningPlatformUrl(state),
  hasLearningPlatformAccess: (state: DisciplineStore): boolean => getDisciplineInfo(state)?.has_learning_platform_access || false,
  isProgressReportsExists: (state: DisciplineStore): boolean => getDisciplineInfo(state)?.progress_reports_exists || false,
  getProgressReportsNewCount: (state: DisciplineStore): number => getDisciplineInfo(state)?.progress_reports_new || 0,
  getAiSummariesNewCount: (state: DisciplineStore): number => getDisciplineInfo(state)?.ai_summaries_new || 0,
  isParentOneOnOneCompleted: (state: DisciplineStore): boolean => getDisciplineInfo(state)?.has_completed_one_to_one || false,
  getDisciplineSchedule: (state: DisciplineStore): DisciplineSchedule | null => getDisciplineInfo(state)?.schedule || null,
  getSummaryInfo: (state: DisciplineStore): DisciplineInfo['bookings_ai_summary'] => getDisciplineInfo(state)?.bookings_ai_summary || [],
  isModalTutorProfile: (state: DisciplineStore): DisciplineStore['modalTutorProfile'] => state.modalTutorProfile,
  ...tutor,
}
